/**
 * RFC-0005 Phase 2 Publish from Ready to List (owner, 2026-10-30): the operator selects cards, reviews a preview and
 * publishes them through the eBay Listing API; the Publish click is the Approve for eBay step.
 */

/** Cards per request; the page sends a selection in chunks of this size. */
export const EBAY_PUBLISH_CARD_LIMIT = 2;

export interface EbayPublishCardRequest {
  cardId: string;
  /** A live CardDealerPro listing the operator paired with this card. */
  ebayItemId?: string;
  /** PUBLISH only: the digest of this card's preview the operator approved. */
  digest?: string;
  /**
   * The operator confirmed none of the possible CardDealerPro listings shown for this card is it, so it lists new.
   * Listings that match by catalog and title still take precedence.
   */
  listNew?: boolean;
}

export interface EbayPublishRequest {
  operation: 'EBAY_PUBLISH';
  mode: 'PREVIEW' | 'PUBLISH';
  cards: EbayPublishCardRequest[];
}

/** What Publish will do with one card. */
export interface EbayPublishCardPreview {
  cardId: string;
  /** PUBLISH a new listing; TAKEOVER ends the CardDealerPro listing first; HELD is not published; DONE already is. */
  action: 'PUBLISH' | 'TAKEOVER' | 'HELD' | 'DONE';
  reasons: string[];
  /** Live CardDealerPro listings of this catalog card left alone because another TCVS card owns them. */
  notes?: string[];
  /** A held card's live CardDealerPro listings the operator can pair it with. */
  candidates?: { ebayItemId: string; title?: string; imageUrl?: string }[];
  /** The card is at Ready to List; Publish records the operator's approval. */
  approvalNeeded: boolean;
  listing?: { title: string; priceMinor: number; category: string; shippingPolicy: string };
  takeover?: {
    ebayItemId: string;
    /** How TCVS knows the CardDealerPro listing is this card. */
    basis: 'LINKED' | 'ONE_TO_ONE' | 'OWNER_PAIR' | 'IDENTICAL_COPIES';
    old?: {
      sku?: string;
      title?: string;
      status?: string;
      priceMinor?: number;
      quantity?: number;
      quantitySold?: number;
      shippingPolicyId?: string;
      offerId?: string;
    };
    priceChanged?: boolean;
  };
}

export interface EbayPublishPreviewCard extends EbayPublishCardPreview {
  digest: string;
  /** eBay's check of the listing in validation mode; absent when the card is held. */
  validation?: { ok: boolean; errors: string[] };
}

export interface EbayPublishPreviewResponse {
  mode: 'PREVIEW';
  cards: EbayPublishPreviewCard[];
}

export interface EbayPublishResult {
  cardId: string;
  /**
   * LISTED / CREATE_FAILED / END_UNCONFIRMED / SOLD_DURING_TAKEOVER come from eBay; STALE means the preview changed
   * (preview again); NOT_STARTED ran out of time (send again); SKIPPED was held.
   */
  outcome: string;
  listingId?: string;
  newEbayItemId?: string;
  detail?: string;
  reasons?: string[];
  errorCode?: string;
  violations?: { code: string; description: string }[];
  preview?: EbayPublishPreviewCard;
}

export interface EbayPublishResponse {
  mode: 'PUBLISH';
  results: EbayPublishResult[];
}
